import React, { useEffect, useId, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";
import { useZoom } from "./ZoomContext";

// Initialize Mermaid once with a black + gold premium theme to match the hub.
let _mermaidReady = false;
function ensureMermaid() {
  if (_mermaidReady) return;
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: "loose",
    theme: "base",
    // Suppress mermaid's built-in error SVG (the bomb icon). We render our
    // own fallback when parse fails, so the library never injects its
    // "Syntax error" SVG into the DOM.
    suppressErrorRendering: true,
    fontFamily: '"Satoshi", ui-sans-serif, system-ui, sans-serif',
    themeVariables: {
      background: "#0A0A0A",
      primaryColor: "#0F0F0E",
      primaryTextColor: "#F5F4F2",
      primaryBorderColor: "#B89846",
      secondaryColor: "#171615",
      secondaryTextColor: "#F5F4F2",
      tertiaryColor: "#0F0F0E",
      tertiaryTextColor: "#F5F4F2",
      lineColor: "#B89846",
      textColor: "#F5F4F2",
      pie1: "#B89846",
      pie2: "#D9B35C",
      pie3: "#8A6F2A",
      pie4: "#E8C879",
      pie5: "#6B5520",
      noteBkgColor: "#171615",
      noteTextColor: "#F5F4F2",
      noteBorderColor: "#B89846",
    },
  });
  _mermaidReady = true;
}

function MermaidBlock({ code }) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [svg, setSvg] = useState("");
  const [failed, setFailed] = useState(false);
  const { openZoom } = useZoom();
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    ensureMermaid();
    (async () => {
      try {
        // Pre-validate so we can fall back BEFORE mermaid renders its own
        // error SVG (the bomb). parse() throws synchronously on bad syntax.
        await mermaid.parse(code);
        const { svg } = await mermaid.render(`m-${id}`, code);
        if (!cancelled) setSvg(svg);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, id]);

  if (failed) {
    // Clean, dignified fallback — no bomb icons, just the original code in a
    // styled block so the response remains useful.
    return (
      <div className="my-3 rounded-xl border border-[#b89846]/30 bg-[#0F0F0E] p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#b89846]/70 font-bold mb-2">
          Diagram source
        </div>
        <pre className="text-xs text-white/85 overflow-x-auto whitespace-pre-wrap break-words font-mono">
          {code}
        </pre>
      </div>
    );
  }

  if (!svg) {
    // Minimal placeholder while async render resolves — avoids layout shift.
    return (
      <div className="my-3 h-24 rounded-xl border border-[#b89846]/25 bg-[#0F0F0E]/60 animate-pulse" />
    );
  }

  return (
    <div
      ref={containerRef}
      role="button"
      tabIndex={0}
      onClick={(e) => openZoom(svg, e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openZoom(svg, e.currentTarget);
        }
      }}
      data-testid="mermaid-diagram"
      title="Click to enlarge"
      className="zoomable my-3 rounded-xl border border-[#b89846]/35 bg-[#0F0F0E] p-3 overflow-x-auto cursor-zoom-in transition-transform hover:scale-[1.02] hover:border-[#b89846]/60"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/**
 * Wraps every markdown <table> in a zoomable container so users can click to
 * inspect wide comparison tables at full size.
 */
function ZoomableTable({ children, ...props }) {
  const ref = useRef(null);
  const { openZoom } = useZoom();
  const handleClick = (e) => {
    if (!ref.current) return;
    // Clone the table's outer HTML so the modal shows a faithful copy
    openZoom(ref.current.outerHTML, e.currentTarget);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(e);
        }
      }}
      title="Click to enlarge"
      className="zoomable my-3 -mx-1 cursor-zoom-in overflow-x-auto rounded-xl border border-[#b89846]/25 transition-colors hover:border-[#b89846]/50"
    >
      <table ref={ref} {...props}>
        {children}
      </table>
    </div>
  );
}

export function Markdown({ children }) {
  return (
    <div className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ inline, className, children: codeChildren, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const lang = match?.[1];
            const raw = String(codeChildren).replace(/\n$/, "");
            if (!inline && lang === "mermaid") {
              return <MermaidBlock code={raw} />;
            }
            return (
              <code className={className} {...props}>
                {codeChildren}
              </code>
            );
          },
          table: ZoomableTable,
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
